import React from 'react'
import styled from '@emotion/styled'
import CustomIcons from '../../ui_reusable/CustomIcons'
import theme from '../../../styles/theme'

// Type definitions
type Quality = 'good' | 'warning' | 'bad'
type Size = 'xs' | 's' | 'm' | 'l' | 'xl'

interface QualityColor {
  background: string
  icon: string
}

interface QualityTagProps {
  quality?: Quality
}

interface QualityBallProps {
  quality?: Quality
  size?: Size
}

// Style configurations
const qualityColors: Record<Quality, QualityColor> = {
  good: {
    background: '#A6F4C5',
    icon: '#12B76A',
  },
  warning: {
    background: '#FEDF89',
    icon: '#F79009',
  },
  bad: {
    background: '#FFE5E5',
    icon: '#FF0000',
  },
}

const ballColors: Record<Quality, string> = {
  good: theme.colors.suplementGreen,
  warning: '#F79009',
  bad: theme.colors.suplementRed,
}

const sizeVariants: Record<Size, string> = {
  xs: '1rem',
  s: '1.5rem',
  m: '2.5rem',
  l: '3.5rem',
  xl: '5rem',
}

// Styled components
const CircleContainer = styled.div<{ quality: Quality }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${(props) => qualityColors[props.quality]?.background};
  display: flex;
  align-items: center;
  justify-content: center;
`

const BallContainer = styled.div<{ quality: Quality; size?: Size }>`
  aspect-ratio: 1;
  width: ${(props) => (props.size ? sizeVariants[props.size] : '100%')};
  height: ${(props) => (props.size ? sizeVariants[props.size] : '100%')};
  border-radius: 50%;
  background-color: ${(props) => ballColors[props.quality]};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

// Helper function to normalize quality strings
const normalizeQuality = (quality: string): Quality => {
  const lowerQuality = quality.toLowerCase()

  if (lowerQuality === 'fix' || lowerQuality === 'low') {
    return 'bad'
  }

  if (lowerQuality === 'moderate' || lowerQuality === 'check') {
    return 'warning'
  }

  return lowerQuality as Quality
}

export const QualityTag: React.FC<QualityTagProps> = ({ quality = 'good' }) => {
  return (
    <CircleContainer quality={quality}>
      <CustomIcons.CheckmarkCircleThickIcon
        size="small"
        color={qualityColors[quality]?.icon}
        clickDisabled={true}
      />
    </CircleContainer>
  )
}

export const QualityBall: React.FC<QualityBallProps> = ({
  quality = 'good',
  size,
}) => {
  const normalizedQuality = normalizeQuality(quality)

  return <BallContainer quality={normalizedQuality} size={size}></BallContainer>
}
